import React from "react";
import {Cell} from "../../../elements/Tables";
import styled from "styled-components";
import PerformanceValue from "../../../performance/PerformanceValue";
import GroupNameCell from "../../grouping/GroupNameCell";
import {NegativeValue, PositiveValue} from "../../../performance/StyledValue";
import {NameContainer, NewIdeasContainer, RankContainer, StatContainer} from "./TableItems";
import TableRow, {ClickablePrimaryRow, PrimaryRow} from "./TableRow";

const PortfolioSummaryTable = (props) => (
    <SummaryTableContainer>
        <SummaryTableSegment {...props}/>
    </SummaryTableContainer>
);

export default PortfolioSummaryTable;

const StyledGroupingTable = styled.div`
    ${ClickablePrimaryRow}:hover ${PositiveValue},
    ${ClickablePrimaryRow}:hover ${NegativeValue} {
        color: white;
    }
`;

const SubgroupName = styled.div`
    padding-left: 24px;
`;

function createValues({ columns, kpi, valueChooser, groupEntry}) {
    return columns.map(c => {
        const isKpi = c.name === kpi;
        const value = valueChooser.valueFor({valueName: c.name, groupingRow: groupEntry});

        return (<StatContainer key={c.name}
                               className={c.className + (isKpi ? " t-kpi" : "")}
                               showKpiBackgroundColour={isKpi}>
            <Cell right>
                <PerformanceValue name={c.name} value={value}/>
            </Cell>
        </StatContainer>);
    })
}

const SummaryTableSegment = ({firstGroupingName, grouping, kpi, valueChooser, currency, shareBased, rowLinkGenerator, columns}) => {
    const rows = grouping;
    const showRank = (firstGroupingName === "author" || firstGroupingName === "author-company");

    const tableRows = [];

    rows.forEach(groupEntry => {
        const wrapWithLink = content => (rowLinkGenerator
                                         ? rowLinkGenerator(groupEntry.groupKey, groupEntry.groupName, content)
                                         : content);

        const shouldBeClickable = (!!rowLinkGenerator && groupEntry.groupName !== "REDACTED");
        const RowComponent = shouldBeClickable ? ClickablePrimaryRow : PrimaryRow;

        const primaryRow = <RowComponent className="t-row"
                                         hasSubgroups={groupEntry.subgrouping && groupEntry.subgrouping.length > 0}
                                         key={groupEntry.groupKey}
                                         clickable={shouldBeClickable}>
            {
                showRank ? <RankContainer className="t-rank">
                             <Rank right>{groupEntry.rank}</Rank>
                         </RankContainer>
                         : null
            }
            <NameContainer>
                <GroupNameCell groupName={groupEntry.groupName}
                               groupClarifier={groupEntry.groupClarifier}/>
            </NameContainer>
            <NewIdeasContainer className="t-new-ideas">
                <Cell right>{groupEntry.numberOfNewIdeas}</Cell>
            </NewIdeasContainer>
            {
                createValues({columns, kpi, groupEntry, valueChooser})
            }
        </RowComponent>;

        tableRows.push(wrapWithLink(primaryRow));

        const subgrouping = groupEntry.subgrouping || [];
        subgrouping.forEach((subGroupEntry, index) => {
            const secondaryRow = (<TableRow className="t-sub-row"
                                            key={groupEntry.groupKey + index}>
                {showRank ? <RankContainer className="t-rank"/> : null}
                <NameContainer>
                    <SubgroupName>
                        <GroupNameCell groupName={subGroupEntry.groupName}
                                       groupClarifier={subGroupEntry.groupClarifier}/>
                    </SubgroupName>
                </NameContainer>
                <NewIdeasContainer className="t-new-ideas">
                    <Cell right>{subGroupEntry.numberOfNewIdeas}</Cell>
                </NewIdeasContainer>
                {
                    createValues({columns, kpi, groupEntry: subGroupEntry, valueChooser})
                }
            </TableRow>);

            tableRows.push(secondaryRow);
        });
    });

    return (
        <StyledGroupingTable className="t-group-summary">
            {tableRows}
        </StyledGroupingTable>
    );
};

const SummaryTableContainer = styled.div`
    flex-direction: column;
    display: flex;
    padding: 0px 0px 12px;
`;

export const Rank = styled(Cell)`
    font-size: 16px;
    font-weight: bold;
`;
